import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteLayout } from "@/components/SiteLayout";
import { PageHero } from "@/components/PageHero";
import { buildBreadcrumbScript } from "@/lib/serviceHubSchema";

const SITE_URL = "https://coloradowebimpressions.com";
const LAST_UPDATED = "May 11, 2026";

export const Route = createFileRoute("/do-not-sell")({
  head: () => ({
    meta: [
      { title: "Your Privacy Choices | Colorado Web Impressions" },
      {
        name: "description",
        content:
          "California (CCPA / CPRA) and Colorado (CPA) privacy choices. How to request access, deletion, or that we do not sell or share your personal information.",
      },
      { name: "robots", content: "index, follow" },
      { property: "og:title", content: "Your Privacy Choices | Colorado Web Impressions" },
      {
        property: "og:description",
        content:
          "Exercise your CCPA / CPRA / CPA rights with Colorado Web Impressions.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: `${SITE_URL}/do-not-sell` },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}/do-not-sell` }],
    scripts: [
      buildBreadcrumbScript([
        { name: "Home", path: "/" },
        { name: "Your Privacy Choices", path: "/do-not-sell" },
      ]),
    ],
  }),
  component: DoNotSellPage,
});

function DoNotSellPage() {
  return (
    <SiteLayout>
      <PageHero
        eyebrow="Legal"
        title="Your Privacy Choices"
        description={`Last updated: ${LAST_UPDATED}. CCPA, CPRA, and Colorado Privacy Act (CPA) rights and how to exercise them.`}
      />

      <section className="bg-background py-16 lg:py-20">
        <div className="mx-auto max-w-3xl px-5 lg:px-8">
          <article className="prose-article">
            <h2>The short version</h2>
            <p>
              <strong>
                Colorado Web Impressions does not sell or &ldquo;share&rdquo;
                (for cross-context behavioral advertising) your personal
                information.
              </strong>{" "}
              We have never sold customer data, and we have no plans to.
            </p>

            <h2>Your rights</h2>
            <p>
              If you are a resident of California, Colorado, or another US state
              with a comprehensive consumer privacy law, you have the right to:
            </p>
            <ul>
              <li>Know what personal information we have about you</li>
              <li>Request a copy of that information</li>
              <li>Ask us to correct or delete it</li>
              <li>
                Opt out of any &ldquo;sale&rdquo; or &ldquo;sharing&rdquo; of
                your personal information (we don&apos;t do either, but the
                right exists)
              </li>
              <li>Not be discriminated against for exercising these rights</li>
            </ul>

            <h2>How to submit a request</h2>
            <p>
              Email us at{" "}
              <a
                href="mailto:privacy@coloradowebimpressions.com?subject=Privacy%20rights%20request"
                className="font-bold text-brand-gold-deep underline"
               aria-label="Email Colorado Web Impressions">
                privacy@coloradowebimpressions.com
              </a>{" "}
              with the subject line &ldquo;Privacy rights request&rdquo; and
              tell us:
            </p>
            <ol>
              <li>Which right you are exercising (access, deletion, etc.)</li>
              <li>Your name, email, and phone (so we can verify it&apos;s you)</li>
              <li>The state you reside in</li>
            </ol>
            <p>
              We will respond within 45 days as required by law and will not
              charge you for honoring a request.
            </p>

            <h2>Authorized agents</h2>
            <p>
              You may use an authorized agent to submit a request on your
              behalf. We will ask for written proof that the agent is
              authorized to act for you before disclosing any information.
            </p>

            <h2>More information</h2>
            <p>
              See{" "}
              <Link to="/privacy" className="font-bold text-brand-gold-deep underline">
                our data practices
              </Link>{" "}
              for details on what we collect, how we use it, and how long we
              keep it.
            </p>
          </article>
        </div>
      </section>
    </SiteLayout>
  );
}
